/**
 * @theme/component — ThemeComponent base class.
 *
 * Every interactive feature is a small custom element extending this class.
 * Web components are the right primitive here because connectedCallback
 * re-fires when the Section Rendering API swaps HTML back in.
 *
 * Provides:
 * - this.refs — elements inside this component carrying a ref="name"
 *   attribute ({ name: element }; ref="name[]" collects an array). Computed
 *   fresh on each access so it stays correct after morphs. Nested custom
 *   elements own their inner refs — a ref belongs to its nearest component.
 * - this.listen(target, type, handler, options?) — addEventListener with the
 *   handler bound to the component, auto-removed on disconnectedCallback.
 *   This is the ONE convention for wiring events; never call addEventListener
 *   directly in a component. Bus subscriptions via subscribe() from
 *   @theme/events should be stored and unsubscribed the same way (see
 *   docs/architecture.md).
 *
 * Subclasses overriding disconnectedCallback MUST call super.disconnectedCallback().
 */

export class ThemeComponent extends HTMLElement {
  #listeners = [];

  /** @returns {Object<string, Element|Element[]>} */
  get refs() {
    const refs = {};
    for (const el of this.querySelectorAll('[ref]')) {
      // Skip refs owned by a nested custom element.
      let owner = el.parentElement;
      while (owner && owner !== this && !owner.localName.includes('-')) {
        owner = owner.parentElement;
      }
      if (owner !== this) continue;

      const name = el.getAttribute('ref');
      if (name.endsWith('[]')) {
        (refs[name.slice(0, -2)] ??= []).push(el);
      } else {
        refs[name] = el;
      }
    }
    return refs;
  }

  /**
   * Listen on any EventTarget; removed automatically when this component
   * disconnects. Returns the bound handler (useful for manual removal).
   * @param {EventTarget} target
   * @param {string} type
   * @param {Function} handler bound to the component instance
   * @param {AddEventListenerOptions} [options]
   */
  listen(target, type, handler, options) {
    const bound = handler.bind(this);
    target.addEventListener(type, bound, options);
    this.#listeners.push([target, type, bound, options]);
    return bound;
  }

  disconnectedCallback() {
    for (const [target, type, bound, options] of this.#listeners) {
      target.removeEventListener(type, bound, options);
    }
    this.#listeners.length = 0;
  }
}
